import { z } from "zod";
import {
    InfiniteData,
    QueryClient,
    useInfiniteQuery,
    useMutation,
    useQueryClient,
    useQuery,
} from "@tanstack/react-query";
import {
    chatContract,
    GetOrCreateSubthreadResponseSchema,
    ListMessagesResponseSchema,
    ListSubthreadMessagesResponseSchema,
    ListThreadsResponseSchema,
} from "@common/contracts/chat.contract";
import { organizationChatContract } from "@common/contracts/organization-chat.contract";
import {
    ListOrganizationThreadsResponseSchema,
    ThreadWithWorkspaceName,
} from "@common/schemas/chat.schema";
import { useScope } from "@state/scopeContext.lib";
import { Message, MessageSchema, MessageType } from "@common/types/chat";
import { SpreadsheetReference } from "@common/schemas/spreadsheetReference.schema";
import { TransactionReference } from "@common/schemas/transactionReference.schema";
import { CounterpartyReference } from "@common/schemas/counterpartyReference.schema";
import { InvoiceReference } from "@common/schemas/invoiceReference.schema";
import { DocumentReference } from "@common/schemas/documentReference.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { tasksQueryKeys, useSetTaskThreadLastReadAtCache } from "./task.api";
import { workspaceQueryKeys } from "./workspace.api";
import { organizationTaskQueryKeys } from "./organizationTask.api";

export function flattenMessagesPages(
    pages: Array<{ messages?: Message[] }> | undefined,
): Message[] {
    if (!pages) {
        return [];
    }
    return pages.flatMap((p) => p.messages ?? []).toReversed();
}

const chatContractClient = getBackendAPIClient(chatContract);
const organizationChatClient = getBackendAPIClient(organizationChatContract);

type WorkspaceThreadsPage = z.infer<typeof ListThreadsResponseSchema>;
type ThreadsPage = Omit<WorkspaceThreadsPage, "threads"> & {
    threads: Array<
        WorkspaceThreadsPage["threads"][number] & ThreadWithWorkspaceName
    >;
};

export function useIsOrganizationChat() {
    const { workspaceId, organizationId } = useScope();
    return !workspaceId && !!organizationId;
}

const threadsQueryKey = (
    activeWorkspaceKey: string,
    organizationId?: string | null,
) => ["chat", "threads", activeWorkspaceKey, organizationId ?? ""];
const unreadMessagesCountKeyPrefix = ["chat", "messages", "unread"];
export const unreadMessagesCountKey = (
    activeWorkspaceKey: string,
    organizationId?: string | null,
) => ["chat", "messages", "unread", activeWorkspaceKey, organizationId ?? ""];

export function useThreads() {
    const { workspaceId, organizationId, activeWorkspaceKey } = useScope();
    const isOrganizationChat = useIsOrganizationChat();

    return useInfiniteQuery<
        ThreadsPage,
        Error,
        InfiniteData<ThreadsPage, string | undefined>,
        string[],
        string | undefined
    >({
        queryKey: threadsQueryKey(activeWorkspaceKey, organizationId),
        queryFn: async ({ pageParam }) => {
            if (isOrganizationChat && organizationId) {
                const res = await organizationChatClient.getThreads({
                    query: {
                        cursor: pageParam,
                        organizationId,
                    },
                });

                return ListOrganizationThreadsResponseSchema.parse(res.body);
            }

            if (!workspaceId) {
                return { threads: [], hasMore: false };
            }

            const res = await chatContractClient.getThreads({
                params: { workspaceId },
                query: {
                    cursor: pageParam,
                    organizationId: organizationId ?? undefined,
                },
            });

            return ListThreadsResponseSchema.parse(res.body);
        },
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        enabled: !!workspaceId || isOrganizationChat,
        initialPageParam: undefined,
    });
}

export function useUnreadMessagesCount() {
    const { workspaceId, activeWorkspaceKey, organizationId } = useScope();
    const isOrganizationChat = useIsOrganizationChat();
    return useQuery({
        queryKey: unreadMessagesCountKey(activeWorkspaceKey, organizationId),
        queryFn: async () => {
            if (isOrganizationChat && organizationId) {
                const res =
                    await organizationChatClient.getNumberOfUnreadMessages({
                        query: { organizationId },
                    });
                return res.body.count;
            }
            if (!workspaceId) {
                return 0;
            }
            const res = await chatContractClient.getNumberOfUnreadMessages({
                params: { workspaceId },
                query: {
                    organizationId: organizationId ?? undefined,
                },
            });
            return res.body.count;
        },
        enabled: !!workspaceId || isOrganizationChat,
        refetchOnMount: "always",
    });
}

const messagesQueryKeyPrefix = (activeWorkspaceKey: string) => [
    "chat",
    activeWorkspaceKey,
    "messages",
];

export const messagesQueryKeyWithOrg = (
    activeWorkspaceKey: string,
    threadId: string,
    organizationId?: string | null,
) => [
    ...messagesQueryKeyPrefix(activeWorkspaceKey),
    threadId,
    organizationId ?? "",
];

export function useMessages(threadId?: string) {
    const { workspaceId, organizationId, activeWorkspaceKey } = useScope();
    return useInfiniteQuery({
        queryKey: messagesQueryKeyWithOrg(
            activeWorkspaceKey,
            threadId ?? "",
            organizationId,
        ),
        queryFn: async ({ pageParam }) => {
            if (!threadId || !workspaceId) {
                return { messages: [], hasMore: false };
            }

            const res = await chatContractClient.getMessages({
                params: { workspaceId, threadId },
                query: {
                    cursor: pageParam as string | undefined,
                    limit: 20,
                    organizationId: organizationId ?? undefined,
                },
            });

            return ListMessagesResponseSchema.parse(res.body);
        },
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        enabled: !!threadId,
        initialPageParam: undefined as string | undefined,
    });
}
interface SendMessageArgs {
    threadId: string;
    content: string;
    type?: MessageType;
    organizationId?: string | null;
    spreadsheetReferences?: SpreadsheetReference[];
    transactionReferences?: TransactionReference[];
    counterpartyReferences?: CounterpartyReference[];
    invoiceReferences?: InvoiceReference[];
    documentReferences?: DocumentReference[];
    financialDocumentIds?: number[];
}

export function useSendMessage() {
    const queryClient = useQueryClient();
    const {
        activeWorkspaceKey,
        workspaceId,
        organizationId: scopeOrganizationId,
    } = useScope();
    return useMutation({
        mutationFn: async ({
            threadId,
            content,
            type,
            organizationId,
            spreadsheetReferences,
            transactionReferences,
            counterpartyReferences,
            invoiceReferences,
            documentReferences,
            financialDocumentIds,
        }: SendMessageArgs) => {
            if (!workspaceId) {
                throw new Error("workspaceId is required");
            }

            const res = await chatContractClient.sendMessage({
                params: { workspaceId, threadId },
                body: {
                    content,
                    type,
                    organizationId: organizationId ?? undefined,
                    spreadsheetReferences,
                    transactionReferences,
                    counterpartyReferences,
                    invoiceReferences,
                    documentReferences,
                    financialDocumentIds,
                },
            });

            return MessageSchema.parse(res.body.message);
        },
        onSuccess: (_message, variables) => {
            queryClient.invalidateQueries({
                queryKey: messagesQueryKeyWithOrg(
                    activeWorkspaceKey,
                    variables.threadId,
                    scopeOrganizationId,
                ),
            });
            queryClient.invalidateQueries({
                queryKey: threadsQueryKey(
                    activeWorkspaceKey,
                    scopeOrganizationId,
                ),
            });
        },
    });
}
interface MarkReadArgs {
    threadId: string;
}

interface InvalidateChatReadStateParams {
    queryClient: QueryClient;
    activeWorkspaceKey: string;
    organizationId?: string | null;
}

function invalidateChatReadState({
    queryClient,
    activeWorkspaceKey,
    organizationId,
}: InvalidateChatReadStateParams) {
    return Promise.all([
        queryClient.invalidateQueries({
            queryKey: unreadMessagesCountKey(
                activeWorkspaceKey,
                organizationId,
            ),
        }),
        queryClient.invalidateQueries({
            queryKey: unreadMessagesCountKeyPrefix,
        }),
        queryClient.invalidateQueries({
            queryKey:
                tasksQueryKeys.threadUnreadCountsByWorkspaceKey(
                    activeWorkspaceKey,
                ),
            exact: false,
        }),
        queryClient.invalidateQueries({
            queryKey: workspaceQueryKeys.closeUnreadCounts(),
            exact: false,
        }),
        queryClient.invalidateQueries({
            queryKey: organizationTaskQueryKeys.all(),
        }),
    ]);
}

export function useMarkRead() {
    const { workspaceId, activeWorkspaceKey, organizationId } = useScope();
    const queryClient = useQueryClient();
    const setTasksCache = useSetTaskThreadLastReadAtCache();
    return useMutation({
        mutationFn: async ({ threadId }: MarkReadArgs) => {
            if (!workspaceId) {
                throw new Error("workspaceId is required");
            }
            const res = await chatContractClient.markRead({
                params: { workspaceId, threadId },
            });

            return res.body.ok;
        },
        onMutate: async ({ threadId }) => {
            await Promise.all([
                queryClient.cancelQueries({
                    queryKey:
                        tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
                    exact: false,
                }),
                queryClient.cancelQueries({
                    queryKey:
                        tasksQueryKeys.threadUnreadCountsByWorkspaceKey(
                            activeWorkspaceKey,
                        ),
                    exact: false,
                }),
            ]);
            setTasksCache(threadId, Date.now());
        },
        onSuccess: (_ok, variables) => {
            queryClient.invalidateQueries({
                queryKey: messagesQueryKeyWithOrg(
                    activeWorkspaceKey,
                    variables.threadId,
                    organizationId,
                ),
            });
            invalidateChatReadState({
                queryClient,
                activeWorkspaceKey,
                organizationId,
            });
        },
    });
}

export function useMarkAllRead() {
    const { workspaceId, activeWorkspaceKey, organizationId } = useScope();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            if (!workspaceId) {
                throw new Error("workspaceId is required");
            }
            const res = await chatContractClient.markAllRead({
                params: { workspaceId },
                query: { organizationId: organizationId ?? undefined },
            });
            if (res.status !== 200) {
                throw new Error("Failed to mark all messages as read");
            }
            return res.body.threadCount;
        },
        onSuccess: () =>
            Promise.all([
                queryClient.invalidateQueries({
                    queryKey: threadsQueryKey(
                        activeWorkspaceKey,
                        organizationId,
                    ),
                }),
                queryClient.invalidateQueries({
                    queryKey: messagesQueryKeyPrefix(activeWorkspaceKey),
                    exact: false,
                }),
                queryClient.invalidateQueries({
                    queryKey:
                        tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
                    exact: false,
                }),
                invalidateChatReadState({
                    queryClient,
                    activeWorkspaceKey,
                    organizationId,
                }),
            ]),
    });
}

interface UpdateMessageArgs {
    threadId: string;
    messageId: string;
    content: string;
}

export function useUpdateMessage() {
    const queryClient = useQueryClient();
    const { workspaceId, organizationId, activeWorkspaceKey } = useScope();
    return useMutation({
        mutationFn: async ({
            threadId,
            messageId,
            content,
        }: UpdateMessageArgs) => {
            if (!workspaceId) {
                throw new Error("workspaceId is required");
            }

            const res = await chatContractClient.updateMessage({
                params: { workspaceId, threadId, messageId },
                body: { content },
            });

            return MessageSchema.parse(res.body.message);
        },
        onMutate: async ({ threadId, messageId, content }) => {
            const queryKey = messagesQueryKeyWithOrg(
                activeWorkspaceKey,
                threadId,
                organizationId,
            );
            await queryClient.cancelQueries({ queryKey });

            const previousData = queryClient.getQueryData(queryKey);

            const updateMessage = (msg: { id: string; content: string }) =>
                msg.id === messageId ? { ...msg, content } : msg;

            queryClient.setQueryData<typeof previousData>(
                queryKey,
                (
                    old:
                        | {
                              pages: Array<{
                                  messages: Message[];
                              }>;
                          }
                        | undefined,
                ) => {
                    if (!old) {
                        return old;
                    }
                    return {
                        ...old,
                        pages: old.pages.map((page) => ({
                            ...page,
                            messages: page.messages.map(updateMessage),
                        })),
                    };
                },
            );

            return { previousData };
        },
        onError: (_err, variables, context) => {
            if (context?.previousData) {
                queryClient.setQueryData(
                    messagesQueryKeyWithOrg(
                        activeWorkspaceKey,
                        variables.threadId,
                        organizationId,
                    ),
                    context.previousData,
                );
            }
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({
                queryKey: messagesQueryKeyWithOrg(
                    activeWorkspaceKey,
                    variables.threadId,
                    organizationId,
                ),
            });
        },
    });
}

interface DeleteMessageArgs {
    threadId: string;
    messageId: string;
}

export function useDeleteMessage() {
    const queryClient = useQueryClient();
    const { workspaceId, organizationId, activeWorkspaceKey } = useScope();
    return useMutation({
        mutationFn: async ({ threadId, messageId }: DeleteMessageArgs) => {
            if (!workspaceId) {
                throw new Error("workspaceId is required");
            }

            const res = await chatContractClient.deleteMessage({
                params: { workspaceId, threadId, messageId },
            });

            return res.body.ok;
        },
        onSettled: (_ok, _error, variables) => {
            queryClient.invalidateQueries({
                queryKey: messagesQueryKeyWithOrg(
                    activeWorkspaceKey,
                    variables.threadId,
                    organizationId,
                ),
            });
            queryClient.invalidateQueries({
                queryKey: threadsQueryKey(activeWorkspaceKey, organizationId),
            });
            queryClient.invalidateQueries({
                queryKey: unreadMessagesCountKey(
                    activeWorkspaceKey,
                    organizationId,
                ),
            });
            queryClient.invalidateQueries({
                queryKey:
                    tasksQueryKeys.threadUnreadCountsByWorkspaceKey(
                        activeWorkspaceKey,
                    ),
                exact: false,
            });
        },
    });
}

interface ResolveMessageArgs {
    threadId: string;
    messageId: string;
}

export function useResolveMessage() {
    const queryClient = useQueryClient();
    const { workspaceId, organizationId, activeWorkspaceKey } = useScope();
    return useMutation({
        mutationFn: async ({ threadId, messageId }: ResolveMessageArgs) => {
            if (!workspaceId) {
                throw new Error("workspaceId is required");
            }
            const res = await chatContractClient.resolveMessage({
                params: { workspaceId, threadId, messageId },
            });
            return MessageSchema.parse(res.body.message);
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({
                queryKey: messagesQueryKeyWithOrg(
                    activeWorkspaceKey,
                    variables.threadId,
                    organizationId,
                ),
            });
            queryClient.invalidateQueries({
                queryKey: threadsQueryKey(activeWorkspaceKey, organizationId),
            });
            queryClient.invalidateQueries({
                queryKey: unreadMessagesCountKey(
                    activeWorkspaceKey,
                    organizationId,
                ),
            });
            queryClient.invalidateQueries({
                queryKey:
                    tasksQueryKeys.threadUnreadCountsByWorkspaceKey(
                        activeWorkspaceKey,
                    ),
                exact: false,
            });
        },
    });
}

export function useUnresolveMessage() {
    const queryClient = useQueryClient();
    const { workspaceId, organizationId, activeWorkspaceKey } = useScope();
    return useMutation({
        mutationFn: async ({ threadId, messageId }: ResolveMessageArgs) => {
            if (!workspaceId) {
                throw new Error("workspaceId is required");
            }
            const res = await chatContractClient.unresolveMessage({
                params: { workspaceId, threadId, messageId },
            });
            return MessageSchema.parse(res.body.message);
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({
                queryKey: messagesQueryKeyWithOrg(
                    activeWorkspaceKey,
                    variables.threadId,
                    organizationId,
                ),
            });
            queryClient.invalidateQueries({
                queryKey: threadsQueryKey(activeWorkspaceKey, organizationId),
            });
            queryClient.invalidateQueries({
                queryKey: unreadMessagesCountKey(
                    activeWorkspaceKey,
                    organizationId,
                ),
            });
            queryClient.invalidateQueries({
                queryKey:
                    tasksQueryKeys.threadUnreadCountsByWorkspaceKey(
                        activeWorkspaceKey,
                    ),
                exact: false,
            });
        },
    });
}

// ── Subthread hooks ──────────────────────────────────────────────────────────

export const subthreadQueryKey = (
    workspaceId: string,
    subthreadId: string,
    organizationId?: string | null,
) => [
    "chat",
    workspaceId,
    "subthread",
    subthreadId,
    "messages",
    organizationId ?? "",
];

const getOrCreateSubthreadQueryKey = (
    workspaceId: string,
    threadId: string,
    messageId: string,
) => ["chat", workspaceId, "subthread-for", threadId, messageId];

type SubthreadData = ReturnType<
    typeof GetOrCreateSubthreadResponseSchema.parse
>["subthread"];

interface MessagesInfiniteCache {
    pages: Array<{
        messages?: Message[];
    }>;
}

function incrementReplyCountForRootMessage({
    cache,
    rootMessageId,
}: {
    cache: MessagesInfiniteCache;
    rootMessageId: string;
}): MessagesInfiniteCache {
    return {
        ...cache,
        pages: cache.pages.map((page) => ({
            ...page,
            messages: (page.messages ?? []).map((message) =>
                message.id === rootMessageId
                    ? {
                          ...message,
                          replyCount: (message.replyCount ?? 0) + 1,
                      }
                    : message,
            ),
        })),
    };
}

export function useGetOrCreateSubthread(threadId: string, messageId: string) {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();
    const queryKey = getOrCreateSubthreadQueryKey(
        workspaceId ?? "",
        threadId,
        messageId,
    );
    const fetchSubthread = async () => {
        if (!workspaceId) {
            throw new Error("workspaceId is required");
        }
        const res = await chatContractClient.getOrCreateSubthread({
            params: { workspaceId, threadId, messageId },
        });
        return GetOrCreateSubthreadResponseSchema.parse(res.body).subthread;
    };

    // Keep a reactive observer for this cache key so setQueryData updates
    // trigger rerenders in consumers (e.g. optimistic replyCount updates).
    const subthreadQuery = useQuery({
        queryKey,
        queryFn: fetchSubthread,
        enabled: false,
        initialData: () => queryClient.getQueryData<SubthreadData>(queryKey),
    });

    const mutation = useMutation({
        mutationFn: fetchSubthread,
        onSuccess: (data) => {
            queryClient.setQueryData<SubthreadData>(queryKey, data);
        },
    });

    return {
        data: mutation.data ?? subthreadQuery.data,
        isLoading: !mutation.data && !subthreadQuery.data && mutation.isPending,
        isError: mutation.isError || subthreadQuery.isError,
        mutate: mutation.mutate,
    };
}

export function useSubthreadMessages(subthreadId?: string) {
    const { workspaceId, organizationId } = useScope();
    return useInfiniteQuery({
        queryKey: subthreadQueryKey(
            workspaceId ?? "",
            subthreadId ?? "",
            organizationId,
        ),
        queryFn: async ({ pageParam }) => {
            if (!subthreadId || !workspaceId) {
                return { messages: [], hasMore: false };
            }
            const res = await chatContractClient.getSubthreadMessages({
                params: { workspaceId, subthreadId },
                query: {
                    cursor: pageParam as string | undefined,
                    limit: 50,
                    organizationId: organizationId ?? undefined,
                },
            });
            return ListSubthreadMessagesResponseSchema.parse(res.body);
        },
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        enabled: !!subthreadId && !!workspaceId,
        initialPageParam: undefined as string | undefined,
    });
}

interface SendSubthreadMessageArgs {
    subthreadId: string;
    threadId: string;
    rootMessageId: string;
    content: string;
    type?: MessageType;
    organizationId?: string | null;
    spreadsheetReferences?: SpreadsheetReference[];
    transactionReferences?: TransactionReference[];
    counterpartyReferences?: CounterpartyReference[];
    invoiceReferences?: InvoiceReference[];
    documentReferences?: DocumentReference[];
    financialDocumentIds?: number[];
}

export function useSendSubthreadMessage() {
    const queryClient = useQueryClient();
    const { workspaceId, activeWorkspaceKey, organizationId } = useScope();
    return useMutation({
        mutationFn: async ({
            subthreadId,
            content,
            type,
            organizationId: msgOrgId,
            spreadsheetReferences,
            transactionReferences,
            counterpartyReferences,
            invoiceReferences,
            documentReferences,
            financialDocumentIds,
        }: SendSubthreadMessageArgs) => {
            if (!workspaceId) {
                throw new Error("workspaceId is required");
            }
            const res = await chatContractClient.sendSubthreadMessage({
                params: { workspaceId, subthreadId },
                body: {
                    content,
                    type,
                    organizationId: msgOrgId ?? undefined,
                    spreadsheetReferences,
                    transactionReferences,
                    counterpartyReferences,
                    invoiceReferences,
                    documentReferences,
                    financialDocumentIds,
                },
            });
            return MessageSchema.parse(res.body.message);
        },
        onMutate: async (variables) => {
            const parentMessagesQueryKey = messagesQueryKeyWithOrg(
                activeWorkspaceKey,
                variables.threadId,
                organizationId,
            );
            const subthreadMetaQueryKey = getOrCreateSubthreadQueryKey(
                workspaceId ?? "",
                variables.threadId,
                variables.rootMessageId,
            );

            await queryClient.cancelQueries({
                queryKey: parentMessagesQueryKey,
            });

            const previousParentMessagesData = queryClient.getQueryData(
                parentMessagesQueryKey,
            );
            const previousSubthreadMetaData = queryClient.getQueryData(
                subthreadMetaQueryKey,
            );

            queryClient.setQueryData(
                parentMessagesQueryKey,
                (old: MessagesInfiniteCache | undefined) => {
                    if (!old) {
                        return old;
                    }

                    return incrementReplyCountForRootMessage({
                        cache: old,
                        rootMessageId: variables.rootMessageId,
                    });
                },
            );

            queryClient.setQueryData(
                subthreadMetaQueryKey,
                (old: SubthreadData | undefined) => {
                    if (!old) {
                        return old;
                    }

                    return {
                        ...old,
                        replyCount: old.replyCount + 1,
                    };
                },
            );

            return {
                previousParentMessagesData,
                previousSubthreadMetaData,
                parentMessagesQueryKey,
                subthreadMetaQueryKey,
            };
        },
        onError: (_error, _variables, context) => {
            if (!context) {
                return;
            }

            queryClient.setQueryData(
                context.parentMessagesQueryKey,
                context.previousParentMessagesData,
            );
            queryClient.setQueryData(
                context.subthreadMetaQueryKey,
                context.previousSubthreadMetaData,
            );
        },
        onSuccess: (_message, variables) => {
            // Refresh subthread messages
            queryClient.invalidateQueries({
                queryKey: subthreadQueryKey(
                    workspaceId ?? "",
                    variables.subthreadId,
                    organizationId,
                ),
            });
            // Invalidate subthread metadata (replyCount) by prefix
            queryClient.invalidateQueries({
                queryKey: [
                    "chat",
                    workspaceId ?? "",
                    "subthread-for",
                    variables.threadId,
                ],
                exact: false,
            });
            // Refresh parent thread messages to update reply count
            queryClient.invalidateQueries({
                queryKey: messagesQueryKeyWithOrg(
                    activeWorkspaceKey,
                    variables.threadId,
                    organizationId,
                ),
            });
        },
    });
}
